// src/pages/admin/Coupons.jsx
import React, { useState, useEffect } from 'react';
import { adminApi } from '../../api/adminApi';
import {
  Box, Paper, Typography, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, IconButton, CircularProgress, Alert, Avatar, Button
} from '@mui/material';
import { Refresh, Cancel, CheckCircle, Store } from '@mui/icons-material';

const AdminCoupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const fetchCoupons = async () => {
    setLoading(true);
    try {
      const res = await adminApi.getAllCoupons();
      setCoupons(res.data || []);
    } catch (err) {
      setError('Failed to load coupons.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCoupons(); }, []);

  const handleToggle = async (id, currentStatus) => {
    try {
      await adminApi.adminToggleCoupon(id, !currentStatus);
      setSuccess(`Coupon ${currentStatus ? 'deactivated' : 'activated'}!`);
      fetchCoupons();
    } catch (err) {
      setError('Failed to toggle coupon status.');
    }
  };

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h5" fontWeight="bold">Platform Coupons</Typography>
            <Typography variant="body2" color="text.secondary">View all coupons created by vendors.</Typography>
          </Box>
          <Button variant="outlined" startIcon={<Refresh />} onClick={fetchCoupons}>Refresh</Button>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: 'grey.50' }}>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Vendor</TableCell>
                <TableCell>Discount</TableCell>
                <TableCell>Used / Limit</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></TableCell></TableRow>
              ) : coupons.length === 0 ? (
                <TableRow><TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>No coupons found on platform.</TableCell></TableRow>
              ) : (
                coupons.map((c) => (
                  <TableRow key={c.id} hover>
                    <TableCell><Typography fontWeight="bold">{c.code}</Typography></TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.main' }}><Store fontSize="small"/></Avatar>
                        {c.Vendor?.storeName || 'N/A'}
                      </Stack>
                    </TableCell>
                    <TableCell>{c.type === 'percentage' ? `${c.value}%` : `$${c.value}`}</TableCell>
                    <TableCell>{c.usedCount || 0} / {c.usageLimit || '∞'}</TableCell>
                    <TableCell><Chip icon={c.isActive ? <CheckCircle /> : <Cancel />} label={c.isActive ? 'Active' : 'Inactive'} size="small" color={c.isActive ? 'success' : 'error'} /></TableCell>
                    <TableCell align="right">
                      <IconButton size="small" color="warning" onClick={() => handleToggle(c.id, c.isActive)}>
                        {c.isActive ? <Cancel /> : <CheckCircle />}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default AdminCoupons;